import { getFunctions, httpsCallable } from "firebase/functions";
import { t } from "i18next";
import { DataTableData, DataTableHeader } from "../../../../../components/deprecated/DataTable/DataTable";
import { PopupTemplates } from "../../../../../components/deprecated/PopupTemplates/PopupTemplates";
import { UserNameLabel } from "../../../../../components/deprecated/Text/Label/UserNameLabel";
import { UserImageView } from "../../../../../components/deprecated/UserImageView/UserImageView";
import { ToastUtilities } from "../../../../../libs/react-toastify/ToastUtilities";
import { TrashcanItemData } from "../../../Trashcan/TrashcanItemData";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { DataObjectAction, DataObjectHelper } from "../../DataObjects/DataObjectHelper";

const functions = getFunctions(undefined, "europe-west1");

const deleteItemPermanently = httpsCallable(functions, "deleteItemPermanently");
const restoreDeletedItems = httpsCallable(functions, "restoreDeletedItems");

export const deletePermanently = (deletedItemID: string) => {
  return ToastUtilities.presentDeletingToast(deleteItemPermanently({ deletedItemID }));
};

export const restoreDeleted = (deletedItemID: string) => {
  return ToastUtilities.presentSavingToast(restoreDeletedItems({ deletedItemID }));
};

export class TrashcanItemDataHelper extends DataObjectHelper<TrashcanItemData> {
  id = () => "trashcanItem";

  allFields(): DataObjectField<any, TrashcanItemData>[] {
    return [];
  }

  displayName = () => t("main.item");

  override getActionsForData = (data: TrashcanItemData): DataObjectAction<TrashcanItemData>[] => {
    const actions: DataObjectAction<TrashcanItemData>[] = [];

    actions.push({
      id: "delete_permanently",
      title: t("main.deletePermanently"),
      iconName: "delete",
      iconColor: "var(--main-red-600)",
      requiredConsent(dataObjects) {
        return new Promise((resolve) => {
          const title =
            dataObjects.length === 1
              ? `${t("main.verifyPermanentDeletion")} ${dataObjects[0].name}?`
              : `${t("main.verifyPermanentDeletion")} ${dataObjects.length} ${t("main.items")}?`;

          PopupTemplates.presentVerifyAction(title, t("main.thisActionCanNotBeUndone"), () => resolve({ dataObjects }));
        });
      },
      performActionOnDataObjects(dataObjects) {
        return Promise.all(dataObjects.map((item) => deletePermanently(item.id)));
      },
    });

    actions.push({
      id: "restore",
      title: t("main.restore"),
      iconName: "restore_from_trash",
      iconColor: "var(--primary-color)",
      requiredConsent(dataObjects) {
        return new Promise((resolve) => {
          const title =
            dataObjects.length === 1
              ? `${t("main.restore")} ${dataObjects[0].name}?`
              : `${t("main.restore")} ${dataObjects.length} ${t("main.items")}?`;

          PopupTemplates.presentVerifyAction(title, t("main.restoreItemFromTrashcanDescription", { name: data.name }), () =>
            resolve({ dataObjects })
          );
        });
      },
      performActionOnDataObjects(dataObjects) {
        return Promise.all(dataObjects.map((item) => restoreDeleted(item.id)));
      },
    });

    return actions;
  };

  getAllDataTableDatas(documents: TrashcanItemData[]): DataTableData[] {
    return documents.map((item) => {
      return {
        key: item.id,
        cells: {
          name: item.name ?? "-",
          type: t(`relativeType.${item.type}`),
          deletedAt: item.deletedBy?.date,
          permanentDeletion: item.permanentDeletion,
          deletedBy: {
            rawString: item.deletedBy?.user?.name,
            element: (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <UserImageView user={item.deletedBy?.user} size={24} fontSize={12} />
                <UserNameLabel user={item.deletedBy?.user} type="paragraph" />
              </div>
            ),
          },
        },
      };
    });
  }

  getAllDataTableHeaders(): DataTableHeader[] {
    return [
      {
        key: "name",
        title: t("main.itemName"),
      },
      {
        key: "type",
        title: t("main.type"),
      },
      {
        key: "deletedAt",
        title: t("main.deletedAt"),
      },
      {
        key: "permanentDeletion",
        title: t("main.permanentDeletion"),
      },
      {
        key: "deletedBy",
        title: t("main.deletedBy"),
      },
    ];
  }
}

